/**
 * Gezählter Bestand — wie viele Stücke eines Pools an einem Tag frei sind.
 *
 * Lisa, Übergabewunsch vom 25.09.2026: *„Muss wirklich jede einzelne Platte
 * eine eigene Zeile und einen eigenen Belegungsbalken bekommen, oder reicht
 * eine Zählgröße (‚wie viele sind für diesen Zeitraum schon vergeben, wie
 * viele noch frei')?"* Es reicht. Ein Pool ist **eine** Ressource mit einer
 * Stückzahl (`machine.pool_stock`, Migration 0194); ihre Belegungen tragen
 * eine Menge (`booking.quantity`).
 *
 * Diese Datei rechnet dieselbe Frage wie der Riegel `booking_im_bestand` in
 * der Datenbank — nur **je Tag** statt auf den Zeitpunkt genau, weil das
 * Brett und die Auskunft am Telefon in Tagen sprechen. Zwei Belegungen, die
 * sich an einem Tag nur nacheinander folgen (vormittags zurück, nachmittags
 * hinaus), zählen hier beide für diesen Tag; die Datenbank lässt sie zu.
 * Die Auskunft ist also nie großzügiger als der Riegel — höchstens
 * vorsichtiger, und das ist die richtige Seite.
 *
 * Dieselben Lagen wie beim Einzelstück (`verfuegbarkeit.ts`):
 *
 *   geht   an jedem Betriebstag des Fensters sind genug Stücke frei
 *   teils  an einem Teil der Tage reicht es, am Rest nicht
 *   nein   an keinem Tag reicht es
 *
 * Vormerkungen blocken nicht (dieselbe Trennung wie im Ausschluss): Sie
 * zählen in `vorgemerkt` und machen aus „frei" ein „vorgemerkt", wenn es
 * mit ihnen nicht mehr reichen würde.
 */
import { tagNummer, ausTagNummer, zaehltAlsMiettag } from "./kalender.js";
import { alsIso } from "./typen.js";
import { HARTE_ARTEN } from "./verfuegbarkeit.js";
const HART = new Set(HARTE_ARTEN);
function istHart(b) {
    return HART.has(b.art);
}
/**
 * Je Kalendertag des Fensters: belegt, vorgemerkt, frei.
 *
 * Reine Funktion, jeder Tag des Fensters kommt vor — auch Wochenenden. Das
 * Brett entscheidet, welche es zeigt; die Lage unten, welche zählen.
 */
export function bestandJeTag(eingabe) {
    const erster = tagNummer(eingabe.von);
    const letzter = tagNummer(eingabe.bis);
    const tage = [];
    for (let n = erster; n <= letzter; n++) {
        let belegt = 0;
        let vorgemerkt = 0;
        for (const b of eingabe.belegungen) {
            if (n < tagNummer(b.von) || n > tagNummer(b.bis))
                continue;
            const menge = Math.max(0, Math.round(b.menge));
            if (istHart(b))
                belegt += menge;
            else if (b.art === 'option')
                vorgemerkt += menge;
        }
        tage.push({
            tag: alsIso(ausTagNummer(n)),
            belegt,
            vorgemerkt,
            frei: Math.max(0, eingabe.bestand - belegt),
            ueber: belegt > eingabe.bestand,
        });
    }
    return tage;
}
/** Zusammenhängende Strecken aus einer aufsteigenden Tagesliste. */
function strecken(tage) {
    const ergebnis = [];
    for (const n of tage) {
        const letzte = ergebnis[ergebnis.length - 1];
        if (letzte !== undefined && n === letzte.bis + 1)
            letzte.bis = n;
        else
            ergebnis.push({ von: n, bis: n });
    }
    return ergebnis;
}
/**
 * Reicht der Bestand für `menge` Stück im Fenster?
 *
 * Gezählt werden nur Betriebstage (derselbe Mietkalender wie beim
 * Einzelstück): Eine Rücknahme am Samstag macht aus einer Woche kein
 * „teils".
 */
export function bestandLage(eingabe) {
    const menge = Math.max(1, Math.round(eingabe.menge));
    const alle = bestandJeTag(eingabe);
    const zaehlt = alle.filter((t) => zaehltAlsMiettag(tagNummer(new Date(`${t.tag}T12:00:00Z`)), eingabe.regeln));
    const basis = {
        bestand: eingabe.bestand,
        menge,
        tage: alle,
    };
    if (zaehlt.length === 0) {
        return {
            ...basis,
            lage: 'geht',
            text_key: 'lage.kein_betriebstag',
            punkt: 'gut',
            frei_min: eingabe.bestand,
            tage_vergeben: 0,
            rand: null,
            warum: [{ key: 'lage.kein_betriebstag', kalendertage: eingabe.regeln.kalendertage }],
        };
    }
    const freiMin = Math.min(...zaehlt.map((t) => t.frei));
    const reicht = zaehlt.filter((t) => t.frei >= menge);
    const nummer = (t) => tagNummer(new Date(`${t.tag}T12:00:00Z`));
    const freieStrecken = strecken(reicht.map(nummer));
    const rand = freieStrecken.length === 0
        ? null
        : `${alsIso(ausTagNummer(freieStrecken[0].von))}/${alsIso(ausTagNummer(freieStrecken[0].bis))}`;
    const tageVergeben = zaehlt.length - reicht.length;
    const warum = [
        { key: 'lage.bestand', bestand: eingabe.bestand, frei: freiMin, menge },
    ];
    for (const s of freieStrecken) {
        warum.push({
            key: 'lage.frei_fenster',
            von: alsIso(ausTagNummer(s.von)),
            bis: alsIso(ausTagNummer(s.bis)),
            tage: s.bis - s.von + 1,
        });
    }
    if (reicht.length === 0) {
        return {
            ...basis, lage: 'nein', text_key: 'lage.bestand_reicht_nicht', punkt: 'stopp',
            frei_min: freiMin, tage_vergeben: tageVergeben, rand: null, warum,
        };
    }
    if (tageVergeben > 0) {
        return {
            ...basis, lage: 'teils', text_key: 'lage.teilweise', punkt: 'acht',
            frei_min: freiMin, tage_vergeben: tageVergeben, rand, warum,
        };
    }
    /* Genug frei — aber reicht es auch, wenn die Vormerkungen zusagen? Wenn
       nicht, ist es dieselbe Auskunft wie beim Einzelstück: frei, aber
       vorgemerkt. Wer zuerst zusagt, bekommt die Platten. */
    const eng = zaehlt.some((t) => t.frei - t.vorgemerkt < menge);
    if (eng) {
        return {
            ...basis, lage: 'geht', text_key: 'lage.vorgemerkt', punkt: 'acht',
            frei_min: freiMin, tage_vergeben: 0, rand, warum,
        };
    }
    return {
        ...basis, lage: 'geht', text_key: 'lage.frei', punkt: 'gut',
        frei_min: freiMin, tage_vergeben: 0, rand, warum,
    };
}
//# sourceMappingURL=bestand.js.map